/**
 * BillingAuditLog — Settings → Billing audit table over `billingEvents`.
 *
 * Surfaces every Stripe audit row for the current org: checkout and
 * customer-create failures (written by `convex/stripe/actions.ts`) plus
 * webhook-driven state changes (`upgrade`, `sync`, `downgrade`,
 * `payment_succeeded`, `payment_failed`, `checkout_completed`). Replaces
 * a Convex-dashboard round-trip for answering "why did the customer's
 * checkout fail at 14:02?".
 */
import * as React from 'react';
import { Receipt } from '@phosphor-icons/react';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Doc, Id } from '@/convex';
import { formatRelativeTime } from '@/lib/format';
import s from './billing.module.scss';

type BillingEvent = Doc<'billingEvents'>;

interface Props {
  orgId: Id<'orgs'>;
}

const FAILURE_TYPE_RE = /_failed$/;

function isFailureType(type: string): boolean {
  return FAILURE_TYPE_RE.test(type);
}

function formatMetadata(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function BillingAuditLog({ orgId }: Props) {
  const [typeFilter, setTypeFilter] = React.useState<string>('');
  const [allItems, setAllItems] = React.useState<BillingEvent[]>([]);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);

  const firstPageArgs = React.useMemo(
    () => ({
      orgId,
      type: typeFilter || undefined,
      cursor: undefined as string | undefined,
    }),
    [orgId, typeFilter],
  );

  const firstPage = useQuery(api.billingEvents.listByOrg, firstPageArgs);

  React.useEffect(() => {
    if (firstPage) {
      setAllItems(firstPage.items as BillingEvent[]);
      setCursor(firstPage.nextCursor);
      setHasMore(firstPage.hasMore);
    }
  }, [firstPage]);

  const nextPage = useQuery(
    api.billingEvents.listByOrg,
    loadingMore && cursor
      ? { orgId, type: typeFilter || undefined, cursor }
      : 'skip',
  );

  React.useEffect(() => {
    if (nextPage && loadingMore) {
      setAllItems((prev) => [...prev, ...(nextPage.items as BillingEvent[])]);
      setCursor(nextPage.nextCursor);
      setHasMore(nextPage.hasMore);
      setLoadingMore(false);
    }
  }, [nextPage, loadingMore]);

  const types = useQuery(api.billingEvents.listTypesByOrg, { orgId });

  const isLoading = firstPage === undefined;

  return (
    <div className={s.billingSection}>
      <div className={s.auditHeader}>
        <div className={s.auditHeaderText}>
          <p className={s.billingSectionTitle}>Billing audit log</p>
          <p className={s.auditDesc}>
            Every Stripe event recorded for this workspace — checkout failures,
            plan changes, and webhook deliveries.
          </p>
        </div>
        {types && types.length > 0 && (
          <label className={s.auditFilterRow}>
            <span>Type</span>
            <select
              className={s.auditFilterSelect}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All</option>
              {types.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {isLoading ? null : allItems.length === 0 ? (
        <div className={s.auditEmpty}>
          <Receipt size={20} weight="duotone" />
          <p style={{ margin: '8px 0 0 0' }}>
            {typeFilter
              ? `No ${typeFilter} events yet.`
              : 'No billing events recorded yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className={s.auditTable}>
            {allItems.map((row) => (
              <div key={row._id} className={s.auditRow}>
                <span
                  className={`${s.auditType} ${
                    isFailureType(row.type) ? s.auditTypeFailure : ''
                  }`}
                >
                  {row.type}
                </span>
                {row.metadata ? (
                  <pre className={s.auditMeta}>
                    {formatMetadata(row.metadata)}
                  </pre>
                ) : (
                  <span className={`${s.auditMeta} ${s.auditMetaEmpty}`}>
                    (no metadata)
                  </span>
                )}
                <span
                  className={s.auditTime}
                  title={new Date(row.createdAt).toISOString()}
                >
                  {formatRelativeTime(row.createdAt)}
                </span>
              </div>
            ))}
          </div>
          {hasMore && (
            <button
              type="button"
              className={s.auditLoadMore}
              onClick={() => !loadingMore && setLoadingMore(true)}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
